import {useEffect, useState} from "react";
import {TenderProcurementsItem} from "../types/tenderProcurements";
import {useForm} from "antd/es/form/Form";
import {openSuccessNotification, showErrorNotification} from "../helpers/mainHelpers";
import {tenderProcurements} from "../services/tenderProcurements-service";
import {Modal} from "antd";
import {DeleteOutlined} from "@ant-design/icons";


export const useTenderProcurementsController = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(0)
  const [tenderProcurementList, setTenderProcurementList] = useState<TenderProcurementsItem[]>([])
  const [selectedTenderProcurementItem, setSelectedTenderProcurementItem] = useState<TenderProcurementsItem | undefined>(undefined)
  const [form] = useForm()

  useEffect(() => {
    if (!isModalOpen) {
      form.resetFields()
      setSelectedTenderProcurementItem(undefined)
    }
  }, [isModalOpen])


  const toggleModal = () => {
    const updatedModalValue = !isModalOpen
    setIsModalOpen(updatedModalValue)
  }

  const toggleLastUpdate = () => {
    setLastUpdate(Date.now())
  }

  const fetchTenderProcurementsList = async (queries: string[]) => {
    setIsLoading(true)
    try {
      const list = await tenderProcurements.list(queries)
      setTenderProcurementList(list)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка при отриманні записів!"})
    } finally {
      setIsLoading(false)
    }
  }

  const createTenderProcurementsItem = async (data: TenderProcurementsItem) => {
    setIsLoading(true)
    try {
      await tenderProcurements.create(data)
      openSuccessNotification("Інформацію по тендерним закупівлям успішно додано!")
      toggleModal()
      toggleLastUpdate()
    } catch (error) {
      showErrorNotification({
        message: "Помилка при додаванні інформації по тендерним закупівлям",
        description: JSON.stringify(error)
      })
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const updateTenderProcurementsItem = async (data: TenderProcurementsItem) => {
    setIsLoading(true)
    try {
      await tenderProcurements.update({$id: selectedTenderProcurementItem?.$id, ...data})
      toggleModal()
      toggleLastUpdate()
      openSuccessNotification("Інформацію по тендерним закупівлям успішно оновлено!")
    } catch (error) {
      console.error(error)
      showErrorNotification({
        message: "Помилка при оновленні інформації по тендерним закупівлям!",
        description: JSON.stringify(error)
      })
    } finally {
      setIsLoading(false)
    }
  }

  const deleteTenderProcurementsItem = async (id: string) => {
    setIsLoading(true)
    try {
      await tenderProcurements.delete(id)
      const updatedList = tenderProcurementList.filter(item => item.$id !== id)
      setTenderProcurementList(updatedList)
      openSuccessNotification("Запис успішно видалено!")
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка видалення запису!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const confirmDelete = (props: { id: string }) => {
    const {confirm} = Modal;
    confirm(
      {
        width: 400,
        icon: <DeleteOutlined/>,
        title: `Видалити запис?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          await deleteTenderProcurementsItem(props.id)
        },
      });
  }

  const downloadButtonHandler = (fileId: string) => {
    const publicUrl = `https://fra.cloud.appwrite.io/v1/storage/buckets/6770fe4400025c7f2afb/files/${fileId}/download?project=67232f8c003094e2430c`
    const a = document.createElement('a')
    a.href = publicUrl
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  const submitButtonHandler = async (formData: { fileId: string, title: string, year: number }) => {
    if (!selectedTenderProcurementItem) {
      await createTenderProcurementsItem(formData)
    } else {
      await updateTenderProcurementsItem(formData)
    }
  }

  const editButtonHandler = (selectedItem: TenderProcurementsItem) => {
    setSelectedTenderProcurementItem(selectedItem)
    toggleModal()
  }

  return {
    isLoading,
    toggleModal,
    isModalOpen,
    selectedTenderProcurementItem,
    form,
    submitButtonHandler,
    tenderProcurementList,
    fetchTenderProcurementsList,
    lastUpdate,
    confirmDelete,
    downloadButtonHandler,
    editButtonHandler
  }
}
